import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../clients/apiClient';
import { useAuthContext } from '../context/AuthContext';

interface TriggerPayload {
  company: string;
  role: string;
  country?: string;
  max_outreach?: number;
  send_mode?: 'draft' | 'send';
  attach_resume?: boolean;
}

type WebhookState = 'idle' | 'loading' | 'success' | 'error';

export type WebhookErrorKind = 'insufficient_credits' | 'gmail_not_connected' | 'generic';

export function useWebhook() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuthContext();
  const [state, setState] = useState<WebhookState>('idle');
  const [errorKind, setErrorKind] = useState<WebhookErrorKind | null>(null);

  async function triggerOutreach(payload: TriggerPayload): Promise<void> {
    setState('loading');
    setErrorKind(null);
    try {
      await apiClient.post('/api/webhook/trigger', payload);
      setState('success');
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '';

      if (status === 402 || /insufficient credits/i.test(detail)) {
        setErrorKind('insufficient_credits');
      } else if (status === 403 || /gmail not connected/i.test(detail)) {
        setErrorKind('gmail_not_connected');
      } else {
        setErrorKind('generic');
      }
      setState('error');
    }
  }

  function reset() {
    setState('idle');
    setErrorKind(null);
  }

  return { triggerOutreach, state, errorKind, reset };
}
